import { ArrowDown } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { plural } from '@/lib/format';
import { Callout } from './callout';

/**
 * Rodapé de lista paginada por cursor: botão "Carregar mais" E um sentinela com
 * IntersectionObserver — rolar até o fim carrega sozinho, e o botão continua
 * lá para teclado e leitor de tela.
 */
export function CarregarMais({
  temMais,
  carregando,
  erro,
  aoCarregar,
  total,
  singular = 'item carregado',
  plural_ = 'itens carregados',
  fim = 'Fim da lista',
  rotuloCarregando = 'Carregando…',
  mensagemErro = 'Não foi possível carregar mais itens',
}: {
  temMais: boolean;
  carregando: boolean;
  erro: Error | null;
  aoCarregar: () => void;
  total: number;
  singular?: string;
  plural_?: string;
  fim?: string;
  rotuloCarregando?: string;
  mensagemErro?: string;
}) {
  const sentinela = React.useRef<HTMLDivElement>(null);
  const aoCarregarRef = React.useRef(aoCarregar);
  aoCarregarRef.current = aoCarregar;

  React.useEffect(() => {
    const alvo = sentinela.current;
    // Erro pausa o automático: tentar de novo sozinho em loop martelaria a API.
    if (!alvo || !temMais || erro || typeof IntersectionObserver === 'undefined') return;
    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((entrada) => entrada.isIntersecting)) aoCarregarRef.current();
      },
      { rootMargin: '400px 0px' },
    );
    observador.observe(alvo);
    return () => observador.disconnect();
  }, [temMais, erro]);

  if (!temMais) {
    return (
      <p className="py-4 text-center text-cell text-on-surface-muted">
        {fim} · {plural(total, singular, plural_)}.
      </p>
    );
  }

  return (
    <div ref={sentinela} className="flex flex-col items-center gap-3 py-2">
      {erro ? (
        <Callout tom="erro" className="w-full">
          {mensagemErro}: {erro.message}
        </Callout>
      ) : null}
      <Button variant="outline" onClick={aoCarregar} loading={carregando}>
        {carregando ? null : <ArrowDown aria-hidden="true" />}
        {carregando ? rotuloCarregando : erro ? 'Tentar de novo' : 'Carregar mais'}
      </Button>
    </div>
  );
}
